import { AfterViewInit, Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

const BREADCRUMB_SEPARATOR = ' / ';

/**
 * Shows the full breadcrumb when it fits. When there is not enough space,
 * keeps the first and last segments fully visible and shortens each middle
 * segment with a trailing " ..." (e.g. "A / B ... / C ... / D").
 */
@Directive({
    selector: '[breadcrumbMiddleTruncate]',
    hostDirectives: [
        {
            directive: MatTooltip,
            inputs: ['matTooltipShowDelay: breadcrumbMiddleTruncateShowDelay'],
        },
    ],
})
export class BreadcrumbMiddleTruncateDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input('breadcrumbMiddleTruncate') fullText?: string | null;

    @Input() breadcrumbMiddleTruncateShowDelay = 500;

    private resizeObserver?: ResizeObserver;

    constructor(
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly matTooltip: MatTooltip
    ) {}

    ngAfterViewInit(): void {
        this.scheduleUpdate();

        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => this.scheduleUpdate());
            this.resizeObserver.observe(this.elementRef.nativeElement);
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.fullText || changes.breadcrumbMiddleTruncateShowDelay) {
            this.scheduleUpdate();
        }
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
    }

    private scheduleUpdate(): void {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => this.update());
        });
    }

    private update(): void {
        const element = this.elementRef.nativeElement;
        const fullText = this.fullText?.trim() || '';

        this.matTooltip.message = fullText;

        if (!fullText) {
            element.textContent = '';
            this.matTooltip.disabled = true;
            return;
        }

        // Wait until the element has a real width before measuring.
        if (element.clientWidth === 0) {
            element.textContent = fullText;
            this.matTooltip.disabled = true;
            return;
        }

        element.textContent = fullText;

        if (!this.overflows(element)) {
            this.matTooltip.disabled = true;
            return;
        }

        const truncated = this.fitBreadcrumb(fullText, element);
        element.textContent = truncated;
        this.matTooltip.disabled = truncated === fullText && !this.overflows(element);
    }

    private fitBreadcrumb(fullText: string, element: HTMLElement): string {
        const parts = fullText.split(BREADCRUMB_SEPARATOR);
        if (parts.length <= 2) {
            return fullText;
        }

        const first = parts[0];
        const last = parts[parts.length - 1];
        const middle = parts.slice(1, -1);
        const maxMiddleLen = Math.max(...middle.map((part) => part.length), 0);

        let best = [first, ...middle.map(() => '...'), last].join(BREADCRUMB_SEPARATOR);
        let low = 0;
        let high = maxMiddleLen;

        while (low <= high) {
            const maxLen = Math.floor((low + high) / 2);
            const candidate = [first, ...middle.map((part) => formatMiddleSegment(part, maxLen)), last].join(BREADCRUMB_SEPARATOR);

            element.textContent = candidate;

            if (!this.overflows(element)) {
                best = candidate;
                low = maxLen + 1;
            } else {
                high = maxLen - 1;
            }
        }

        return best;
    }

    private overflows(element: HTMLElement): boolean {
        return element.scrollWidth > element.clientWidth;
    }
}

function formatMiddleSegment(segment: string, maxLen: number): string {
    if (maxLen <= 0) {
        return '...';
    }

    const prefix = segment.slice(0, Math.min(maxLen, segment.length)).trimEnd();
    return `${prefix} ...`;
}
